import React, { useState, useRef, useCallback } from "react";
import { Button, Spinner, Table, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Upload, FileText, CheckCircle, XCircle, ArrowLeft, Download } from "lucide-react";
import apiClient from "../../utils/apiClient";
import { PageHeader, BackButton } from "../ui";
import CommonToaster from "../Common/CommonToaster";
import "./QuizBank.scss";

const SAMPLE_CSV = `questionText,questionType,difficulty,marks,optionA,optionB,optionC,optionD,correctOptions,explanation,tags
"What is 2+2?",single_choice,easy,1,"3","4","5","6","B","2+2 equals 4","math,arithmetic"
"Select all prime numbers",multi_select,medium,2,"2","4","5","9","A,C","2 and 5 are prime","math,primes"
"JavaScript is a compiled language",true_false,easy,1,"True","False","","","B","JS is interpreted","javascript"`;

const BulkUploadPage = () => {
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [file, setFile] = useState(null);
  const [parsedQuestions, setParsedQuestions] = useState([]);
  const [errors, setErrors] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const parseCSV = useCallback((text) => {
    const lines = text.split("\n").filter(l => l.trim());
    if (lines.length < 2) {
      setErrors(["CSV must have a header row and at least one data row"]);
      return [];
    }

    const headers = lines[0].split(",").map(h => h.trim().replace(/^"|"$/g, ""));
    const questions = [];
    const parseErrors = [];

    for (let i = 1; i < lines.length; i++) {
      try {
        // Simple CSV parse handling quoted fields
        const values = [];
        let current = "";
        let inQuotes = false;
        for (const char of lines[i]) {
          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === "," && !inQuotes) {
            values.push(current.trim());
            current = "";
          } else {
            current += char;
          }
        }
        values.push(current.trim());

        const row = {};
        headers.forEach((h, idx) => {
          row[h] = values[idx] || "";
        });

        // Build question object
        const options = [];
        ["optionA", "optionB", "optionC", "optionD"].forEach((key, idx) => {
          if (row[key]) {
            options.push({ text: row[key], isCorrect: false });
          }
        });

        // Mark correct options
        const correctStr = (row.correctOptions || row.correct || "").toUpperCase();
        const correctIndexes = correctStr.split(",").map(c => c.trim().charCodeAt(0) - 65);
        correctIndexes.forEach(ci => {
          if (options[ci]) options[ci].isCorrect = true;
        });

        const q = {
          questionText: row.questionText || "",
          questionType: row.questionType || "single_choice",
          difficulty: row.difficulty || "medium",
          marks: parseFloat(row.marks) || 1,
          negativeMarks: parseFloat(row.negativeMarks) || 0,
          options,
          explanation: row.explanation || "",
          tags: (row.tags || "").split(",").map(t => t.trim()).filter(Boolean),
        };

        if (!q.questionText) {
          parseErrors.push(`Row ${i + 1}: Missing question text`);
        } else {
          questions.push(q);
        }
      } catch (err) {
        parseErrors.push(`Row ${i + 1}: Parse error`);
      }
    }

    setErrors(parseErrors);
    return questions;
  }, []);

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    setFile(selected);
    setResult(null);
    setErrors([]);

    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      if (selected.name.endsWith(".json")) {
        try {
          const data = JSON.parse(text);
          const questions = Array.isArray(data) ? data : data.questions || [];
          setParsedQuestions(questions);
          if (questions.length === 0) {
            setErrors(["No questions found in JSON file"]);
          }
        } catch {
          setErrors(["Invalid JSON file"]);
          setParsedQuestions([]);
        }
      } else {
        const questions = parseCSV(text);
        setParsedQuestions(questions);
      }
    };
    reader.readAsText(selected);
  };

  const handleUpload = async () => {
    if (parsedQuestions.length === 0) return;
    setUploading(true);

    try {
      const { data } = await apiClient.post("/quiz-questions/bulk-upload", {
        questions: parsedQuestions,
      });
      setResult(data);
      setToast({ show: true, message: `${data.inserted || data.count || parsedQuestions.length} questions uploaded`, type: "success" });
    } catch (err) {
      const msg = err.response?.data?.message || "Upload failed";
      const serverErrors = err.response?.data?.errors || [];
      setErrors(serverErrors.length > 0 ? serverErrors.map(e => e.message || e) : [msg]);
      setToast({ show: true, message: msg, type: "error" });
    }
    setUploading(false);
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "quiz_questions_sample.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="add-edit-quiz-question-page">
      <BackButton />
      <PageHeader
        icon={Upload}
        title="Bulk Upload Questions"
        description="Upload multiple questions from a CSV or JSON file"
      />

      {/* Sample Download */}
      <div className="mb-4">
        <Button variant="outline-secondary" size="sm" onClick={downloadSample}>
          <Download size={14} className="me-1" /> Download Sample CSV
        </Button>
      </div>

      {/* Upload Dropzone */}
      <div className="bulk-upload-modal">
        <div
          className="upload-dropzone"
          onClick={() => fileRef.current?.click()}
          role="button"
          tabIndex={0}
        >
          <div className="upload-dropzone__icon">
            <FileText size={40} />
          </div>
          <div className="upload-dropzone__text">
            {file ? file.name : "Click to select a CSV or JSON file"}
          </div>
          <div className="upload-dropzone__hint">
            Supported formats: .csv, .json — Max 500 questions per upload
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.json"
            onChange={handleFileChange}
            className="d-none"
          />
        </div>

        {/* Errors */}
        {errors.length > 0 && (
          <div className="upload-errors mt-3">
            {errors.map((err, i) => (
              <div className="error-item" key={i}>
                <XCircle size={14} className="me-1" /> {err}
              </div>
            ))}
          </div>
        )}

        {/* Preview Table */}
        {parsedQuestions.length > 0 && !result && (
          <>
            <div className="mt-3 mb-2 d-flex justify-content-between align-items-center">
              <span className="fw-semibold">{parsedQuestions.length} questions parsed</span>
              <Button variant="primary" onClick={handleUpload} disabled={uploading}>
                {uploading ? <Spinner size="sm" animation="border" /> : "Upload All"}
              </Button>
            </div>
            <div className="preview-table">
              <Table size="sm" bordered hover>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Question</th>
                    <th>Type</th>
                    <th>Difficulty</th>
                    <th>Options</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedQuestions.slice(0, 50).map((q, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td className="text-truncate" style={{ maxWidth: 300 }}>{q.questionText}</td>
                      <td>{q.questionType}</td>
                      <td>{q.difficulty}</td>
                      <td>{q.options?.length || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {parsedQuestions.length > 50 && (
                <div className="text-muted small text-center">
                  Showing first 50 of {parsedQuestions.length} questions
                </div>
              )}
            </div>
          </>
        )}

        {/* Success Result */}
        {result && (
          <Alert variant="success" className="mt-3">
            <CheckCircle size={18} className="me-2" />
            Successfully uploaded {result.inserted || result.count || parsedQuestions.length} questions!
            <div className="mt-2">
              <Button size="sm" variant="primary" onClick={() => navigate("/quiz-bank")}>
                Go to Question Bank
              </Button>
            </div>
          </Alert>
        )}
      </div>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />
    </div>
  );
};

export default BulkUploadPage;
